// /compare/vidflow: Pixally vs VidFlow.
//
// Read the header of honeybook.js first: every rule there applies here. VidFlow's
// facts, with sources and exact words: docs/geo/compare-videographer-crm-evidence.md
// and docs/geo/compare-gallery-platforms-evidence.md.
//
// VidFlow is a delivery platform, not a CRM, and its pricing model (upload packs,
// two years of access the client can extend) is the same shape as Pixally's. So
// the honest comparison is: VidFlow is the specialist delivery tool, with smart
// TV apps that Pixally does not have; Pixally's packs cost a little less and come
// inside a CRM. Both stream and download in 4K (Pixally: config/pricingPlans.js,
// "Downloads in 4K, for the whole window"). Never claim TV apps for Pixally.

export const ROUTE = '/compare/vidflow';
export const UPDATED_AT = '2026-10-09';
export const CHECKED_AT = 'October 2, 2026';
export const CHECKED_LABEL = 'VidFlow facts checked';
export const HERO_ART = 'reel';

export const TITLE = 'Pixally vs VidFlow';
export const SEO_TITLE = 'Pixally vs VidFlow for Wedding Film Delivery';
export const SEO_DESCRIPTION =
  'How Pixally and VidFlow compare for delivering wedding films: upload packs, access, TV apps, photos alongside films, and running the studio, with every VidFlow fact sourced.';

export const LEDE =
  'VidFlow is a delivery platform built for wedding filmmakers: upload packs with no subscription, two years of access the couple can extend, 4K streaming and apps for smart TVs. Pixally, launching on January 1, 2027, delivers films the same way, packs and a two-year window the couple can extend, but inside a CRM that also runs the booking, the contract, the payments, the edit and the crew.';

const VIDFLOW_PRICING = 'https://vidflow.co/pricing';

export const BODY = [
  {
    type: 'paragraph',
    text: 'If you already have a CRM you like and want the most polished way to put a film on a couple\'s TV, VidFlow is built for exactly that. If you would rather deliver from the same place you book, bill and edit, the differences below matter.',
  },
  { type: 'heading', level: 2, text: 'Side by side' },
  {
    type: 'paragraph',
    text: `The VidFlow column comes from VidFlow's own pricing page, checked ${CHECKED_AT}.`,
  },
  {
    type: 'table',
    label: 'Pixally and VidFlow compared',
    head: ['', 'Pixally', 'VidFlow'],
    rows: [
      ['What it is', 'A CRM for wedding photographers and videographers, with delivery built in', 'A delivery platform for wedding filmmakers and hybrid teams'],
      ['Available', 'From January 1, 2027', 'Now'],
      ['Delivery pricing', '50 GB included once on paid plans; packs: 50 GB $50, 100 GB $95, 500 GB $450', 'Packs: 50 GB $60, 100 GB $110, 500 GB $475; no subscription'],
      ['Subscription', 'Free plan; Basic $33, Pro $58, Studio $99 a month billed yearly', 'None for packs; 1 TB+ annual plans on request'],
      ['How long films stay live', 'Two years from delivery; the couple can extend', 'Two years; clients can extend'],
      ['Film length', 'No limit', 'Not described on the pricing page'],
      ['Photos alongside films', 'Photo, video and hybrid galleries', 'Photo delivery as well as video'],
      ['4K streaming and downloads', 'Yes, for as long as the gallery is live; the camera original for the first 90 days', 'Yes'],
      ['Smart TV apps', 'No', 'Yes'],
      ['Embedding', 'Embed hosting for your website: 3 films on Free, then 60, 90 or 120 minutes by paid plan', 'Embeddable galleries'],
      ['Bookings, contracts, payments', 'Built in', 'Not a CRM'],
      ['Second shooters and post-production', 'Contractor portal, second shooter management and post-production tracking on Pro and Studio', 'Not a CRM'],
    ],
  },
  { type: 'heading', level: 2, text: 'When VidFlow is the better choice' },
  {
    type: 'list',
    items: [
      'It exists today. Pixally launches on January 1, 2027.',
      'Apps for smart TVs, so a couple can watch the film on the television. Pixally does not have them.',
      'You already run your studio in a CRM you like and only need delivery. VidFlow has no subscription for its packs.',
      'It is used by more than 15,000 wedding filmmakers and hybrid teams, by its own count.',
    ],
  },
  { type: 'heading', level: 2, text: 'When Pixally is the better choice' },
  {
    type: 'paragraph',
    text: 'Pixally is the better choice if you want delivery in the same system as the rest of the studio, or a little more film for the money.',
  },
  { type: 'heading', level: 3, text: 'Delivery inside the CRM' },
  { type: 'art', name: 'pipeline' },
  {
    type: 'paragraph',
    text: 'With VidFlow, the booking, contract and payments live in a separate CRM. In Pixally the same project carries the inquiry, proposal, contract, payments, the edit through post-production (Pro and Studio) and the delivered film.',
  },
  { type: 'heading', level: 3, text: 'Packs that cost a little less' },
  { type: 'art', name: 'film' },
  {
    type: 'paragraph',
    text: "Pixally's packs follow the same model, upload once and keep two years of access the couple can extend, at $50 for 50 GB, $95 for 100 GB and $450 for 500 GB, against VidFlow's $60, $110 and $475. Every paid Pixally plan also starts with 50 GB included.",
  },
  { type: 'heading', level: 3, text: 'Your crew' },
  { type: 'art', name: 'crew' },
  {
    type: 'paragraph',
    text: 'Second shooters get a [free contractor account](/contractor-terms) that works across every studio they shoot for, with their availability, travel fee and tax form in one place. Second shooter management and the contractor portal are on Pro (up to 10) and Studio (unlimited).',
  },
];

export const FAQ = [
  {
    q: 'How much does VidFlow cost?',
    a: 'VidFlow has no subscription for its packs: 50 GB for $60, 100 GB for $110 and 500 GB for $475, each with two years of access for your couples, which they can extend. Annual plans of 1 TB or more are available on request.',
  },
  {
    q: 'Does VidFlow deliver photos too?',
    a: 'Yes. VidFlow offers photo delivery as well as video, for wedding filmmakers and hybrid teams.',
  },
  {
    q: 'Is VidFlow a CRM?',
    a: 'No. VidFlow is a delivery platform. Bookings, contracts and payments need a separate CRM.',
  },
  {
    q: 'Does Pixally have TV apps?',
    a: 'No. Pixally streams and downloads films in 4K in the browser, but has no smart TV apps; VidFlow does.',
  },
  {
    q: 'Is Pixally available yet?',
    a: 'No. Pixally launches on January 1, 2027. You can join the waitlist at pixally.com.',
  },
];

export const SOURCES = [
  { label: 'VidFlow pricing', url: VIDFLOW_PRICING },
  { label: 'Pixally plans and video packs: the Pixally pricing table', url: null },
  { label: 'Pixally contractor accounts: Contractor Terms of Use, Section 5.1', url: '/contractor-terms' },
];
